import { useEffect, useMemo, useState, useCallback, useRef, forwardRef, useImperativeHandle } from 'react';
import usePlanningGroupData from './hooks/usePlanningGroupData';
import usePlanIndexColumns from './hooks/usePlanIndexColumns';
import fetchPlanList from './hooks/usePlanList';
import { Popover, Spin } from 'antd';
import { type PlanRecord } from '../types/plan';
import { Table } from 'antd';
import type { ColumnType } from 'antd/es/table';
import type { SorterResult } from 'antd/es/table/interface';
import ChevronDown from 'assets/svg/ChevronDown';
import { getOptimizationStatusIcon } from 'utils/optimizationStatusUtils';
import FileIcon from 'assets/svg/FileIcon';
import Folder from 'assets/svg/Folder';
import EditIcon from 'assets/svg/EditIcon';
import { MoreOutlined, PlusCircleOutlined } from '@ant-design/icons';
import { Link, useLocation } from 'react-router-dom';
import InfiniteScroller from 'components/molecules/InfiniteScroller';
import { useTpoGenConfig } from '../context/CustomerConfigContext';
import type { PlanIndexColumnConfig } from '../config/tpoGenConfig';
import { uniqueId } from 'lodash';

const isChildRow = (record: PlanRecord) => record.plan_count == null;

const mapApiPlansToRecords = (
    plans: any[],
    groupId: string,
    planIndexColumns: PlanIndexColumnConfig[],
): PlanRecord[] => {
    const plansList = plans.map((plan) => {
        const record: PlanRecord = {
            planning_group_id: plan.plan_id,
            planning_group_name: plan.plan_name,
            optimisation_status: plan.optimisation_status,
        };
        for (const col of planIndexColumns) {
            record[col.dataIndex] = plan[col.apiField];
        }
        return record;
    });
    plansList.push({
        planning_group_id: uniqueId('create-new-plan-'),
        planning_group_name: 'Create New Plan',
        is_create_action: true,
        parent_group_id: groupId,
    });
    return plansList;
};

export interface PlansIndexTableHandle {
    refreshExpandedGroups: () => Promise<void>;
}

const PlansIndexTable = forwardRef<
    PlansIndexTableHandle,
    {
        searchKeyword: string;
        showMyPlans: boolean;
        selectedColumns: string[];
        filters: Record<string, any>;
        setCreatePlanGroupDetails: (group: PlanRecord | null) => void;
        setCreatePlanModalOpen: (open: boolean) => void;
        setEditPlanDetails: (plan: PlanRecord | null) => void;
    }
>(
    (
        {
            searchKeyword,
            showMyPlans,
            selectedColumns,
            filters,
            setCreatePlanGroupDetails,
            setCreatePlanModalOpen,
            setEditPlanDetails,
        },
        ref,
    ) => {
        const tpoGenConfig = useTpoGenConfig();
        const { planIndexColumns } = tpoGenConfig;
        const { search } = useLocation();
        const [expandedKeys, setExpandedKeys] = useState<React.Key[]>([]);
        const [plansByGroup, setPlansByGroup] = useState<Record<string, PlanRecord[]>>({});
        const [sortBy, setSortBy] = useState<string>('last_updated');
        const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
        const [planListLoading, setPlanListLoading] = useState<boolean>(false);

        const refreshExpandedGroups = useCallback(async () => {
            for (const groupId of expandedKeys) {
                const id = String(groupId);
                const data = await fetchPlanList(id, showMyPlans, filters, sortBy, sortOrder);
                setPlansByGroup((prev) => ({ ...prev, [id]: mapApiPlansToRecords(data.plans, id, planIndexColumns) }));
            }
        }, [expandedKeys, showMyPlans, sortBy, sortOrder, filters, planIndexColumns]);

        useImperativeHandle(ref, () => ({ refreshExpandedGroups }), [refreshExpandedGroups]);

        const {
            data: planningGroupData,
            isPending: planningGroupDataLoading,
            isFetching: planningGroupDataFetching,
            fetchNextPage,
            hasNextPage,
            isFetchingNextPage,
        } = usePlanningGroupData({
            searchKey: searchKeyword,
            myPlansOnly: showMyPlans,
            filters,
            sortBy,
            sortOrder,
        });

        useEffect(() => {
            setExpandedKeys([]);
            setPlansByGroup({});
        }, [searchKeyword, showMyPlans, filters, sortBy, sortOrder]);

        const dataSource = useMemo(() => {
            return (planningGroupData ?? []).map((group: PlanRecord) => {
                const groupId = group.planning_group_id;
                if (!expandedKeys.includes(groupId)) return group;
                const fetched = plansByGroup[groupId];
                if (fetched) {
                    return { ...group, children: [...fetched] };
                }
                return group;
            });
        }, [planningGroupData, plansByGroup, expandedKeys]);

        const toggleExpand = useCallback(
            async (groupId: string) => {
                if (expandedKeys.includes(groupId)) {
                    setExpandedKeys((prev) => prev.filter((k) => k !== groupId));
                    setPlansByGroup((prev) => {
                        const next = { ...prev };
                        delete next[groupId];
                        return next;
                    });
                    return;
                }

                setExpandedKeys((prev) => [...prev, groupId]);
                setPlanListLoading(true);
                const data = await fetchPlanList(groupId, showMyPlans, filters, sortBy, sortOrder);
                setPlansByGroup((prev) => ({
                    ...prev,
                    [groupId]: mapApiPlansToRecords(data.plans, groupId, planIndexColumns),
                }));
                setPlanListLoading(false);
            },
            [expandedKeys, showMyPlans, filters, sortBy, sortOrder, planIndexColumns],
        );

        const handleTableChange = useCallback(
            (_pagination: any, _filters: any, sorter: SorterResult<PlanRecord> | SorterResult<PlanRecord>[]) => {
                const singleSorter = Array.isArray(sorter) ? sorter[0] : sorter;
                if (!singleSorter.order) {
                    setSortBy('last_updated');
                    setSortOrder('desc');
                    return;
                }
                setSortBy(singleSorter.field as string);
                setSortOrder(singleSorter.order === 'ascend' ? 'asc' : 'desc');
            },
            [],
        );

        const optionalColumns = usePlanIndexColumns(selectedColumns, sortBy, sortOrder);

        const columns = useMemo(() => {
            const sortOrder_ =
                sortBy === 'planning_group_name'
                    ? sortOrder === 'asc'
                        ? ('ascend' as const)
                        : ('descend' as const)
                    : null;

            const nameColumn: ColumnType<PlanRecord> = {
                title: tpoGenConfig.labels.planIndex.table.planningGroups,
                width: 500,
                dataIndex: 'planning_group_name',
                key: 'name',
                sorter: true,
                sortDirections: ['descend', 'ascend'],
                sortOrder: sortOrder_,
                ellipsis: true,
                render: (text: string, record: PlanRecord) => {
                    if (record.is_loading) {
                        return <Spin size="small" />;
                    }

                    if (record.is_create_action) {
                        return (
                            <div className="ml-14">
                                <button
                                    type="button"
                                    className="flex cursor-pointer items-center gap-x-2 border-0 bg-transparent p-0 text-zinc-700 hover:text-zinc-900"
                                    onClick={() => {
                                        const parentGroup = dataSource.find(
                                            (g: PlanRecord) => g.planning_group_id === record.parent_group_id,
                                        );
                                        setCreatePlanGroupDetails(parentGroup ?? null);
                                        setEditPlanDetails(null);
                                        setCreatePlanModalOpen(true);
                                    }}
                                >
                                    <PlusCircleOutlined style={{ fontSize: 16 }} />
                                    <span className="">{text}</span>
                                </button>
                            </div>
                        );
                    }

                    const isChild = isChildRow(record);
                    const isExpanded = expandedKeys.includes(record?.planning_group_id);

                    return (
                        <div className="flex flex-row items-center gap-x-2" key={record?.planning_group_id}>
                            {!isChild && (
                                <button
                                    type="button"
                                    className="flex h-5 w-5 shrink-0 cursor-pointer items-center justify-center rounded border-0 bg-transparent p-0 text-zinc-900 hover:text-zinc-600"
                                    aria-expanded={isExpanded}
                                    aria-label={
                                        isExpanded
                                            ? tpoGenConfig.labels.planIndex.table.collapse
                                            : tpoGenConfig.labels.planIndex.table.expand
                                    }
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        toggleExpand(record?.planning_group_id);
                                    }}
                                >
                                    <ChevronDown className={isExpanded ? 'rotate-180' : 'rotate-0'} size={12} />
                                </button>
                            )}
                            {isChild ? (
                                <span className="flex h-8 w-8 shrink-0 self-center">
                                    {getOptimizationStatusIcon(record.optimisation_status)}
                                </span>
                            ) : (
                                <span className="inline-block w-5 shrink-0" />
                            )}

                            <div className="shrink-0 self-center">{isChild ? <FileIcon size={16} /> : <Folder />}</div>

                            {isChild ? (
                                <Link
                                    to={`/tpo-gen/plan/${record.planning_group_id}`}
                                    state={{
                                        planId: record.planning_group_id.toString(),
                                        planName: text,
                                        fromSearch: search,
                                    }}
                                >
                                    <span className="cursor-pointer underline text-sm truncate">{text}</span>
                                </Link>
                            ) : (
                                <span className="text-sm">{text}</span>
                            )}
                        </div>
                    );
                },
            };

            const actionsColumn: ColumnType<PlanRecord> = {
                title: '',
                width: 48,
                fixed: 'right',
                key: 'actions',
                render: (_: unknown, record: PlanRecord) => {
                    if (record.is_loading || record.is_create_action) return null;

                    if (isChildRow(record)) {
                        return (
                            <button
                                type="button"
                                className="flex h-8 w-8 cursor-pointer items-center justify-center rounded border-0 bg-transparent p-0 text-zinc-900 hover:text-zinc-600"
                                aria-label={tpoGenConfig.labels.planIndex.table.editPlan}
                                onClick={() => {
                                    const parentGroup = dataSource.find((g: PlanRecord) =>
                                        g.children?.some(
                                            (child) => child.planning_group_id === record.planning_group_id,
                                        ),
                                    );
                                    setCreatePlanGroupDetails(parentGroup ?? null);
                                    setEditPlanDetails(record);
                                    setCreatePlanModalOpen(true);
                                }}
                            >
                                <EditIcon size={16} />
                            </button>
                        );
                    }
                    return null;
                },
            };

            return [nameColumn, ...optionalColumns, actionsColumn];
        }, [
            optionalColumns,
            sortBy,
            sortOrder,
            expandedKeys,
            dataSource,
            search,
            setCreatePlanGroupDetails,
            setEditPlanDetails,
            setCreatePlanModalOpen,
            toggleExpand,
        ]);
        return (
            <InfiniteScroller
                data={dataSource}
                loading={isFetchingNextPage}
                hasMore={hasNextPage ?? false}
                height={window.innerHeight - 370}
                onNext={() => fetchNextPage()}
            >
                <Table<PlanRecord>
                    rowKey="planning_group_id"
                    columns={columns}
                    dataSource={dataSource}
                    pagination={false}
                    loading={
                        planningGroupDataLoading ||
                        (planningGroupDataFetching && !isFetchingNextPage) ||
                        planListLoading
                    }
                    onChange={handleTableChange}
                    expandable={{
                        expandedRowKeys: expandedKeys,
                        expandIcon: () => null,
                        rowExpandable: (record) =>
                            !record.is_loading && !record.is_create_action && !isChildRow(record),
                        onExpand: (_, record) => {
                            if (!record.is_loading && !record.is_create_action && !isChildRow(record)) {
                                toggleExpand(record.planning_group_id);
                            }
                        },
                    }}
                    sticky={{
                        getContainer: () =>
                            document.getElementsByClassName('infinite-scroll-component')?.[0] as HTMLElement,
                    }}
                    scroll={{ x: '100%' }}
                />
            </InfiniteScroller>
        );
    },
);

PlansIndexTable.displayName = 'PlansIndexTable';

export default PlansIndexTable;
